import React from 'react';
import {
  Menu,
  Popup,
  Icon,
  Header,
  Dropdown,
  Container,
} from 'semantic-ui-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { PAGE_SIZE } from '../constants';
import { NAV } from '../navLabels';
import V2chatSessionCreateModal from '../features/v2/chat_sessions/v2ChatSessionCreateModal';
import { logOut } from '../features/auth/authSlice';
import { BRAND } from '../brand';
import { VERSION_LABEL } from '../version';
import MobileNavMenu from './MobileNavMenu';
import RedDot from './RedDot';
import { useGetSupportUnreadCountQuery } from '../features/support_tickets/supportTicketApiSlice';

const Navbar = () => {
  const [isUserCardOpen, setIsUserCardOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  const hasToken = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.user);
  // Unread support replies (#271). Polled: an admin's reply should show up without a reload.
  const { data: unreadSupport } = useGetSupportUnreadCountQuery(undefined, {
    skip: !hasToken,
    pollingInterval: 120000,
  });
  const hasUnreadSupport = (unreadSupport?.count || 0) > 0;
  const partner = useSelector(state => state.auth.partner);

  // Mono-channel 파트너 (Astore, Frisbee) do not sell across the product range, so their
  // trainees do not see the quiz menu. Defaults to shown when the flag is absent — an older
  // API response or a user with no contractor must not silently lose the menu.
  const showKnowledgeTraining =
    user?.contractor?.knowledge_training_enabled !== false;

  // One binding for both menu trees (mobile and desktop are fully duplicated below).
  const contractorLabel = user?.is_superuser ? NAV.CONTRACTORS : NAV.CONTRACTOR_ACCESS;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    dispatch(logOut());
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  let content;

  const adminDropdownStyle = `
    .ui.menu .ui.dropdown.item.admin-menu-dropdown {
      height: 100% !important; 
      min-height: auto !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      display: flex !important;
      align-items: center !important;
    }
    
    .ui.menu .ui.dropdown.item.admin-menu-dropdown:hover {
        background: rgba(0,0,0,0.03) !important;
    }
  `;

  if (!hasToken) {
    content = (
      <Menu
        size="massive"
        fixed="top"
        borderless
        style={{
          backgroundColor: '#f5f5f7',
          borderBottom: '0px solid #d2d2d7',
          boxShadow: 'none',
        }}
      >
        <Menu.Item as={Link} to="/" header>
          <img
            src="/ace-logo.png"
            alt={BRAND}
            style={{ height: '24px', width: 'auto', marginRight: '10px' }}
          />
          {BRAND}
        </Menu.Item>

        {/* right side */}
        <Menu.Item position="right" as={Link} to="/login">
          로그인
        </Menu.Item>
      </Menu>
    );
  } else {
    if (isMobile) {
      content = (
        <>
          <Menu
            size="massive"
            fixed="top"
            borderless
            style={{
              backgroundColor: '#f5f5f7',
              borderBottom: '0px solid #d2d2d7',
              boxShadow: 'none',
            }}
          >
            <Menu.Item
              header
              as={Link}
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img
                src="/ace-logo.png"
                alt={BRAND}
                style={{ height: '24px', width: 'auto', marginRight: '10px' }}
              />
              {BRAND}
            </Menu.Item>
            <Menu.Menu position="right">
              {/* Becomes an ✕ while open: the sheet covers the screen, so the same button
                  is the only way back and should say so. */}
              <Menu.Item onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} style={{ position: 'relative' }}>
                <Icon name={isMobileMenuOpen ? 'close' : 'sidebar'} />
                {hasUnreadSupport && !isMobileMenuOpen && (
                  <RedDot style={{ position: 'absolute', top: '13px', right: '13px' }} />
                )}
              </Menu.Item>
            </Menu.Menu>
          </Menu>

          {isMobileMenuOpen && (
            <MobileNavMenu
              user={user}
              partner={partner}
              showKnowledgeTraining={showKnowledgeTraining}
              contractorLabel={contractorLabel}
              hasUnreadSupport={hasUnreadSupport}
              onNavigate={() => setIsMobileMenuOpen(false)}
              onLogout={handleLogout}
            />
          )}
        </>
      );
    } else {
      content = (
        <>
          <Menu
            size="large"
            fixed="top"
            borderless
            style={{
              backgroundColor: '#f5f5f7',
              borderBottom: '0px solid #d2d2d7',
              boxShadow: 'none',
            }}
          >
            <Container
              fluid
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 4em',
                alignItems: 'center',
              }}
            >
              <Menu.Item header as={Link} to="/" style={{ marginRight: '3em' }}>
                <img
                  src="/ace-logo.png"
                  alt={BRAND}
                  style={{ height: '24px', width: 'auto', marginRight: '10px' }}
                />
                {BRAND}
              </Menu.Item>

              <Menu.Item
                as={Link}
                to="/simulation"
                style={{
                  fontWeight:
                    location.pathname.includes('/simulation') ||
                    location.pathname.includes('/customerpersonascards')
                      ? 700
                      : 500,
                }}
              >
                {NAV.ROLEPLAY}
              </Menu.Item>
              {showKnowledgeTraining && (
                <Menu.Item
                  as={Link}
                  to="/quiz/dashboard"
                  style={{
                    fontWeight: location.pathname.includes('/quiz/dashboard')
                      ? 700
                      : 500,
                  }}
                >
                  {NAV.QUIZ}
                </Menu.Item>
              )}
              <Menu.Item
                as={Link}
                to={`/chatsessionslist?page=1&page_size=${PAGE_SIZE}`}
                style={{
                  fontWeight: location.pathname.includes('/chatsessionslist')
                    ? 700
                    : 500,
                }}
              >
                {NAV.SESSIONS}
              </Menu.Item>



              {/* <Menu.Item
                as={Link}
                to={`/evaluationsessionslist?page=1&page_size=${PAGE_SIZE}`}
                style={{
                  fontWeight: location.pathname.includes(
                    '/evaluationsessionslist',
                  )
                    ? 700
                    : 500,
                }}
              >
                AI 평가
              </Menu.Item> */}

              {(user.is_staff || user.is_contractor_admin) && (
                <Dropdown
                  item
                  text="관리자 메뉴"
                  simple
                  style={{
                    fontWeight: 500,
                  }}
                >
                  <Dropdown.Menu>
                    <Dropdown.Header>운영</Dropdown.Header>
                    {(user.is_superuser || user.is_contractor_admin) && (
                      <Dropdown.Item as={Link} to="/admin/contractors">{contractorLabel}</Dropdown.Item>
                    )}
                    {user.is_superuser && (
                      <Dropdown.Item as={Link} to="/admin/support-tickets">지원 요청</Dropdown.Item>
                    )}
                    {(user.is_staff || user.is_contractor_admin) && (
                      <Dropdown.Item as={Link} to={`/userslist?page=1&page_size=${PAGE_SIZE}`}>{NAV.MY_STAFF}</Dropdown.Item>
                    )}
                    {user.is_staff && (
                      <>
                    <Dropdown.Divider />
                    <Dropdown.Header>분석</Dropdown.Header>
                    <Dropdown.Item as={Link} to={`/partnerevaluatedsessionlist?page=1&page_size=${PAGE_SIZE}`}>AI vs 수기 평가 상세 비교</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/alignment-study">AI vs 수기 평가 일치도 분석</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/pk-alignment-study">제품 지식 vs 판매 스킬 상관관계</Dropdown.Item>
                      </>
                    )}
                    {user.is_staff && (
                      <>
                    <Dropdown.Item as={Link} to={`/customerpersonaslist?page=1&page_size=${PAGE_SIZE}`}>페르소나</Dropdown.Item>
                    <Dropdown.Item as={Link} to={`/evaluationcriteriagrouplist?page=1&page_size=${PAGE_SIZE}`}>평가 기준</Dropdown.Item>

                    <Dropdown.Divider />

                    <Dropdown.Header>제품 지식</Dropdown.Header>
                    <Dropdown.Item as={Link} to="/admin/products">제품 정보 관리</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/admin/quiz-questions">퀴즈 질문</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/admin/quiz-attempts">퀴즈 내역</Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}

              {/* right side */}

              {hasToken ? (
                <Menu.Item
                  onClick={() => setIsUserCardOpen(!isUserCardOpen)}
                  style={{ marginLeft: '3em' }}
                >
                  {/* Logout */}
                  <Menu.Header style={{ marginRight: 5 }}>
                    {user.full_name ? user.full_name : user.username}
                  </Menu.Header>
                  <Popup
                    on="click"
                    position="bottom right"
                    pinned
                    onClose={() => setIsUserCardOpen(false)}
                    onOpen={() => setIsUserCardOpen(true)}
                    open={isUserCardOpen}
                    as={Menu}
                    vertical
                    trigger={
                      <span style={{ position: 'relative', display: 'inline-block' }}>
                        <Icon fitted name="user circle outline" />
                        {hasUnreadSupport && (
                          <RedDot style={{ position: 'absolute', top: '-2px', right: '-4px' }} />
                        )}
                      </span>
                    }
                    style={{ padding: 0 }}
                  >
                    {/* <Menu vertical> */}
                    <Menu.Item>
                      <Header>
                        {user.full_name ? user.full_name : user.username}
                        <Header.Subheader>{user.email}</Header.Subheader>
                        <Header.Subheader>{partner?.name || ''}</Header.Subheader>{' '}
                      </Header>
                    </Menu.Item>

                    <Menu.Item as={Link} to="/my-feedback">
                      <Icon name="comment alternate" />
                      내 피드백
                      {hasUnreadSupport && <RedDot style={{ marginLeft: '8px', verticalAlign: 'middle' }} />}
                    </Menu.Item>

                    <Menu.Item as={Link} to="/userdashboard">
                      <Icon name="dashboard" />
                      나의 대시보드
                    </Menu.Item>

                    <Menu.Item onClick={handleLogout}>
                      <Icon name="logout" />
                      로그아웃
                    </Menu.Item>

                    {/* Last, and not a Menu.Item: this is a label, so it must not take
                        hover/click affordances that suggest it does something. */}
                    <div
                      style={{
                        padding: '8px 14px',
                        borderTop: '1px solid rgba(34,36,38,.1)',
                        fontSize: '11px',
                        color: '#86868b',
                        textAlign: 'center',
                      }}
                    >
                      {VERSION_LABEL}
                    </div>
                    {/* </Menu> */}
                  </Popup>
                </Menu.Item>
              ) : (
                <Menu.Item as={Link} to="/login">
                  로그인
                </Menu.Item>
              )}
            </Container>
          </Menu>
        </>
      );
    }
  }

  return content;
};

export default Navbar;
